import React, { useState, useEffect } from "react";
import { Title, useDataProvider, useNotify, Loading } from "react-admin";
import {
  Card,
  CardContent,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Divider,
  Box,
  TextField,
} from "@mui/material";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { exportLedgerToExcel } from "../utils/ledgerExportExcel";
import { exportLedgerToPDF } from "../utils/ledgerExportPDF";

function formatDate(d) {
  if (!d) return "-";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "-";
  return dt.toLocaleDateString();
}

function cleanDescriptionForLedger(txn) {
  const desc = (txn?.description || "").toString();
  if ((txn?.expenseType || "") === "Auto Mirror") {
    const m = desc.match(/Original:\s*([^|]+)/i);
    if (m && m[1]) return m[1].trim();
  }
  return desc.trim();
}

function cleanCommentForLedger(txn) {
  if ((txn?.expenseType || "") === "Auto Mirror") return "";
  return (txn?.comment || "").toString().trim();
}

function getTxnType(txn) {
  return (txn?.expenseType || txn?.type || txn?.category || "").toString().trim();
}

function formatAED(n) {
  const num = Number(n || 0);
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function toDateObj(value) {
  if (!value) return null;
  const d = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const LedgerList = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [accounts, setAccounts] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [ledgerRows, setLedgerRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [broughtForwardBalance, setBroughtForwardBalance] = useState(0);

  useEffect(() => {
    dataProvider
      .getList("accounts", {
        pagination: { page: 1, perPage: 300 },
        sort: { field: "name", order: "ASC" },
      })
      .then(({ data }) => setAccounts(data))
      .catch((err) => notify("Error loading accounts: " + err.message, { type: "warning" }));
  }, [dataProvider, notify]);

  useEffect(() => {
    async function loadTypes() {
      try {
        const snap = await getDocs(collection(db, "transactions"));
        const set = new Set();
        snap.docs.forEach((d) => {
          const t = getTxnType(d.data());
          if (t) set.add(t);
        });
        setTypes(Array.from(set).sort((a, b) => a.localeCompare(b)));
      } catch (err) {
        console.error("Failed to load transaction types", err);
      }
    }
    loadTypes();
  }, []);

  useEffect(() => {
    const fetchLedger = async () => {
      if (!startDate || !endDate) return;
      if (!selectedAccount && !selectedType) {
        setLedgerRows([]);
        return;
      }

      setLoading(true);
      try {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        const typeFiltered = Boolean(selectedType);
        let accountNameById = {};
        accounts.forEach((a) => (accountNameById[a.id] = a.name));

        let accOpening = 0;
        if (selectedAccount) {
          const accResp = await dataProvider.getOne("accounts", { id: selectedAccount });
          const accData = accResp.data;
          accOpening = Number(accData.openingBalance || 0);
        }
        setOpeningBalance(typeFiltered ? 0 : accOpening);

        let txnQuery;
        if (selectedAccount) {
          txnQuery = query(
            collection(db, "transactions"),
            where("accountId", "==", selectedAccount),
            orderBy("date", "asc")
          );
        } else {
          txnQuery = query(collection(db, "transactions"), orderBy("date", "asc"));
        }

        const snap = await getDocs(txnQuery);
        let allTxns = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        if (selectedType) {
          const target = selectedType.toLowerCase().trim();
          allTxns = allTxns.filter((txn) => getTxnType(txn).toLowerCase().trim() === target);
        }

        const cleaned = allTxns.map((txn) => ({
          ...txn,
          accountName: accountNameById[txn.accountId] || txn.accountName || "",
          ledgerType: getTxnType(txn),
          amountIn: Number(txn.amountIn || 0),
          amountOut: Number(txn.amountOut || 0),
          dateObj: toDateObj(txn.date),
        }));

        const beforeRange = [];
        const inRange = [];
        cleaned.forEach((t) => {
          if (!t.dateObj || isNaN(t.dateObj.getTime())) return;
          if (t.dateObj < start) beforeRange.push(t);
          else if (t.dateObj >= start && t.dateObj <= end) inRange.push(t);
        });

        // If Type filter is selected, show movement report only. Do not mix
        // account opening balance with a subcategory/type ledger.
        const sumBeforeIn = beforeRange.reduce((a, b) => a + b.amountIn, 0);
        const sumBeforeOut = beforeRange.reduce((a, b) => a + b.amountOut, 0);
        const bfBalance = typeFiltered ? 0 : accOpening + sumBeforeIn - sumBeforeOut;
        setBroughtForwardBalance(bfBalance);

        let running = bfBalance;
        const dataRows = inRange
          .sort((a, b) => {
            const d = a.dateObj - b.dateObj;
            if (d !== 0) return d;
            const aIn = a.amountIn > 0;
            const bIn = b.amountIn > 0;
            if (aIn !== bIn) return aIn ? -1 : 1;
            const aAmt = (a.amountIn || 0) + (a.amountOut || 0);
            const bAmt = (b.amountIn || 0) + (b.amountOut || 0);
            return bAmt - aAmt;
          })
          .map((txn) => {
            running = running + txn.amountIn - txn.amountOut;
            return {
              isBFRow: false,
              date: txn.dateObj,
              formattedDate: txn.dateObj.toLocaleDateString(),
              accountName: txn.accountName,
              expenseType: txn.ledgerType,
              description: cleanDescriptionForLedger(txn),
              amountIn: txn.amountIn,
              amountOut: txn.amountOut,
              runningBalance: running,
              paidBy: txn.paidBy || "",
              comment: cleanCommentForLedger(txn),
            };
          });

        const rows = typeFiltered
          ? dataRows
          : [
              {
                isBFRow: true,
                date: null,
                formattedDate: "-",
                accountName: selectedAccount ? accountNameById[selectedAccount] || "" : "",
                expenseType: "B/F Balance",
                description: `Balance brought forward up to ${start.toLocaleDateString()}`,
                amountIn: 0,
                amountOut: 0,
                runningBalance: bfBalance,
                paidBy: "-",
                comment: "-",
              },
              ...dataRows,
            ];

        setLedgerRows(rows);
      } catch (err) {
        console.error(err);
        notify("Error loading ledger: " + err.message, { type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchLedger();
  }, [selectedAccount, selectedType, startDate, endDate, dataProvider, notify, accounts]);

  if (loading && !ledgerRows.length) return <Loading />;

  const monthRows = ledgerRows.filter((r) => !r.isBFRow);
  const totalIn = monthRows.reduce((a, b) => a + (b.amountIn || 0), 0);
  const totalOut = monthRows.reduce((a, b) => a + (b.amountOut || 0), 0);
  const closingBalance =
    monthRows.length > 0
      ? monthRows[monthRows.length - 1].runningBalance
      : broughtForwardBalance;

  const accountName = selectedAccount
    ? accounts.find((a) => a.id === selectedAccount)?.name || "Account"
    : "All Accounts";
  const reportTitle = selectedType
    ? `${accountName} - Type: ${selectedType}`
    : accountName;

  return (
    <Card sx={{ m: 3 }}>
      <CardContent>
        <Title title="Account Ledger" />
        <Typography variant="h5" gutterBottom>
          Ledger Report
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 260 }}>
            <InputLabel>Account Head</InputLabel>
            <Select
              value={selectedAccount}
              label="Account Head"
              onChange={(e) => {
                setSelectedAccount(e.target.value);
                setLedgerRows([]);
              }}
            >
              <MenuItem value="">All accounts</MenuItem>
              {accounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 240 }}>
            <InputLabel>Type / Subcategory</InputLabel>
            <Select
              value={selectedType}
              label="Type / Subcategory"
              onChange={(e) => {
                setSelectedType(e.target.value);
                setLedgerRows([]);
              }}
            >
              <MenuItem value="">All types</MenuItem>
              {types.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
        </Box>

        {!selectedAccount && !selectedType && startDate && endDate && (
          <Typography sx={{ mb: 2, color: "#b45309" }}>
            Please select Account Head or Type / Subcategory to generate ledger.
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <button
            onClick={() => exportLedgerToExcel(ledgerRows, selectedAccount, startDate, endDate, reportTitle, selectedType)}
            disabled={!ledgerRows.length}
            style={{ padding: "8px 14px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
          >
            Download Excel
          </button>
          <button
            onClick={() => exportLedgerToPDF(ledgerRows, startDate, endDate, broughtForwardBalance, totalIn, totalOut, closingBalance, reportTitle, selectedType)}
            disabled={!ledgerRows.length}
            style={{ padding: "8px 14px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
          >
            Download PDF
          </button>
        </Box>

        {(selectedAccount || selectedType) && (
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Report: <strong>{reportTitle}</strong>
            <br />
            {selectedType ? (
              <span>Type-filtered ledger shows movement only; account opening balance is not mixed into the type report.</span>
            ) : (
              <>
                Opening Balance: <strong>{formatAED(openingBalance)} AED</strong>
                <br />
                Balance brought forward before this period: <strong>{formatAED(broughtForwardBalance)} AED</strong>
              </>
            )}
          </Typography>
        )}

        {!startDate || !endDate ? null : loading ? (
          <CircularProgress />
        ) : ledgerRows.length === 0 ? (
          <Typography>No transactions found for this range.</Typography>
        ) : (
          <>
            <Table size="small" sx={{ mb: 3, border: "1px solid #ccc", borderCollapse: "collapse", "& th, & td": { border: "1px solid #ccc", padding: "6px 10px" }, "& th": { backgroundColor: "#f3f3f3", fontWeight: "bold", textAlign: "center" }, "& td": { textAlign: "center" } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount In</TableCell>
                  <TableCell align="right">Amount Out</TableCell>
                  <TableCell align="right">Running Balance</TableCell>
                  <TableCell>Paid By</TableCell>
                  <TableCell>Comments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledgerRows.map((row, idx) => (
                  <TableRow key={idx} sx={row.isBFRow ? { backgroundColor: "#fafafa", fontWeight: "bold" } : row.expenseType === "Auto Mirror" ? { backgroundColor: "#f9f9f9", color: "#777" } : {}}>
                    <TableCell>{row.isBFRow ? "-" : formatDate(row.date)}</TableCell>
                    <TableCell>{row.accountName || accountName || "-"}</TableCell>
                    <TableCell>{row.expenseType || "-"}</TableCell>
                    <TableCell>{row.description || "-"}</TableCell>
                    <TableCell align="right">{row.amountIn ? formatAED(row.amountIn) : "-"}</TableCell>
                    <TableCell align="right">{row.amountOut ? formatAED(row.amountOut) : "-"}</TableCell>
                    <TableCell align="right">{formatAED(row.runningBalance)}</TableCell>
                    <TableCell>{row.paidBy || "-"}</TableCell>
                    <TableCell>{row.comment || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1"><strong>Total In:</strong> {formatAED(totalIn)} AED</Typography>
            <Typography variant="body1"><strong>Total Out:</strong> {formatAED(totalOut)} AED</Typography>
            <Typography variant="h6" sx={{ mt: 1 }}><strong>Closing Balance:</strong> {formatAED(closingBalance)} AED</Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};
